import { logger } from '@adornis/base/logging.js';
import { Arg, Entity, Mutation } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { serverContext } from '@adornis/baseql/server/context.js';
import { runInContext } from '@adornis/baseql/server/server.js';
import { _subscribeCurrentUser, currentUser, updateCurrentUser } from '@adornis/users/client/currentUser.js';
import { loginWithToken } from '@adornis/users/db/a-user.js';
import { generateLoginToken } from '@adornis/users/server/token.js';
import { filter, firstValueFrom } from 'rxjs';
import { UserNotFoundError } from '../errors/UserNotFoundError.js';
import { notifyOtp } from '../notification/otp.js';
import { createOTP, deleteOTP, useOTPOnly } from './otp-tokens.js';
import { User } from './user.js';

@Entity()
export class RegistrationConfirmationCode extends AdornisEntity {
  static override _class = 'RegistrationConfirmationCode';

  @Mutation(type => Boolean)
  static sendOTP(
    @Arg('userID', type => String) userID?: string,
    @Arg('email', type => String) email?: string,
    @Arg('institutionID', type => String) institutionID?: string,
  ) {
    return async () => {
      if (!userID && !email) throw new Error('Needs userID or email, to send OTP');

      if (!userID) {
        const regex = { $regex: `^${email!.replace(/([^\w]{1})/g, '\\$1')}$`, $options: 'i' };
        userID = await (await getRawCollection<User>(User._collectionName))
          .findOne({ $or: [{ email: regex }, { username: regex }] })
          .then(user => user?._id);
      }
      if (!userID) throw new UserNotFoundError();

      const code = await createOTP({ userID });
      try {
        // run in server context because user is not logged in yet, and cant access necessary data
        await runInContext(serverContext.sessionID, () => notifyOtp(userID!, code, institutionID));
      } catch (e) {
        logger.error(e);
        await deleteOTP({ userID, otp: code });
        return false;
      }
      return true;
    };
  }

  @Mutation(type => String)
  static validateOTP(@Arg('otp', type => String) otp: string) {
    return async () => {
      const userID = await useOTPOnly(otp);

      // mark email as confirmed
      const userCollection = await getRawCollection(User._collectionName);
      await userCollection.updateOne({ _id: userID }, { $set: { emailConfirmed: true } });

      return generateLoginToken({ userID, userClass: User._class, ttu: 1 });
    };
  }

  static async loginWithOTP(otp: string) {
    const loginToken = await this.validateOTP(otp)();
    const [userID, userClass] = await loginWithToken(loginToken)();
    updateCurrentUser(undefined, false);
    await _subscribeCurrentUser({ userID, userClass });
    return firstValueFrom(currentUser<User>().whenReady.pipe(filter(Boolean)));
  }
}
